import type { CSSProperties } from 'react';
import clsx from 'clsx';

import type { FlowStep, FlowVisual } from './flowSteps';
import styles from './HowItWorksFlow.module.css';

interface HowItWorksFlowProps {
  /** Steps to render on the rail (mirrors the backend pipeline). */
  steps: ReadonlyArray<FlowStep>;
  /** Active step index — driven by the live job/discovery stage. */
  activeIndex: number;
  /** Live label shown in the card title (e.g. the stage label or progress message). */
  caption?: string | undefined;
}

/** Detected-field highlight delays for the OCR beat (mirrors the landing's `--d`). */
function detStyle(delay: string): CSSProperties {
  return { '--d': delay } as CSSProperties;
}

/** The realistic invoice mock shared by the document (idle) and OCR-scan states. */
function Invoice({ ocr = false }: { ocr?: boolean }): JSX.Element {
  const det = (delay: string): { className?: string | undefined; style?: CSSProperties | undefined } =>
    ocr ? { className: styles.det, style: detStyle(delay) } : {};
  return (
    <div className={styles.inv}>
      <div className={styles.invHead}>
        <div className={styles.invEmisor}>
          <span className={styles.invLogo}>M</span>
          <div>
            <b {...det('0s')}>Mesaches S.L.</b>
            <span {...det('.1s')}>CIF B-99XXXXXX</span>
          </div>
        </div>
        <div className={styles.invTitle}>
          <b>FACTURA</b>
          <span {...det('.18s')}>Nº 2026-0412</span>
        </div>
      </div>
      <div className={styles.invMeta}>
        <div>
          <span>Fecha</span>
          <b {...det('.28s')}>12/04/2026</b>
        </div>
        <div>
          <span>Vencimiento</span>
          <b>12/05/2026</b>
        </div>
        <div className={styles.invCli}>
          <span>Cliente</span>
          <b>Tecnologías del Ebro S.A.</b>
        </div>
      </div>
      <table className={styles.invTable}>
        <thead>
          <tr>
            <th>Concepto</th>
            <th>Uds</th>
            <th>Importe</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Licencia plataforma · anual</td>
            <td>1</td>
            <td>7.800,00</td>
          </tr>
          <tr>
            <td>Implantación y formación</td>
            <td>1</td>
            <td>2.514,05</td>
          </tr>
        </tbody>
      </table>
      <div className={styles.invTot}>
        <div>
          <span>Base imponible</span>
          <b>10.314,05 €</b>
        </div>
        <div>
          <span>IVA 21%</span>
          <b>2.165,95 €</b>
        </div>
        <div className={styles.invGrand}>
          <span>TOTAL</span>
          <b {...det('.4s')}>12.480,00 €</b>
        </div>
      </div>
      <div className={styles.invIban}>
        <span>IBAN</span>
        {ocr ? <span {...det('.5s')}>ES·· ···· ···· ···· 4021</span> : 'ES·· ···· ···· ···· 4021'}
      </div>
    </div>
  );
}

/** Ordered visual palette → each maps to a `data-state` so the CSS keyframes fire. */
const VISUAL_STATE: Record<FlowVisual, number> = {
  invoice: 0,
  scan: 1,
  fields: 2,
  checks: 3,
  meters: 4,
  json: 5,
};

function VisualState({ visual, active }: { visual: FlowVisual; active: boolean }): JSX.Element {
  return (
    <div
      className={clsx(styles.vstate, active && styles.isOn)}
      data-state={VISUAL_STATE[visual]}
      aria-hidden={!active}
    >
      {visual === 'invoice' ? (
        <div className={styles.vpage}>
          <Invoice />
        </div>
      ) : null}
      {visual === 'scan' ? (
        <div className={styles.vpage}>
          <span className={styles.vscan} />
          <Invoice ocr />
        </div>
      ) : null}
      {visual === 'fields' ? (
        <div className={styles.vfields}>
          {[
            ['proveedor', 'Mesaches S.L.'],
            ['cif', 'B-99XXXXXX'],
            ['total', '12.480,00 €'],
            ['fecha_emisión', '2026-04-12'],
            ['iban', 'ES·· ···· 4021'],
          ].map(([k, v]) => (
            <div key={k} className={styles.vrow}>
              <span className={styles.vk}>{k}</span>
              <span className={styles.vv}>{v}</span>
            </div>
          ))}
        </div>
      ) : null}
      {visual === 'checks' ? (
        <div className={styles.vfields}>
          {[
            ['proveedor', 'ok'],
            ['cif', 'ok'],
            ['total', 'ok'],
            ['fecha_emisión', 'warn'],
            ['iban', 'warn'],
          ].map(([k, tone]) => (
            <div key={k} className={clsx(styles.vrow, tone === 'ok' ? styles.ok : styles.warn)}>
              <span className={styles.vk}>{k}</span>
              <span className={styles.vchk}>{tone === 'ok' ? '✓' : '!'}</span>
            </div>
          ))}
        </div>
      ) : null}
      {visual === 'meters' ? (
        <div className={styles.vscore}>
          {[
            ['proveedor', '96', 'ok'],
            ['cif', '92', 'ok'],
            ['total', '98', 'ok'],
            ['fecha', '71', 'warn'],
            ['iban', '64', 'warn'],
          ].map(([k, w, tone]) => (
            <div key={k} className={styles.vmeter}>
              <span className={styles.vk}>{k}</span>
              <i
                className={tone === 'ok' ? styles.ok : styles.warn}
                style={{ '--w': `${w}%` } as CSSProperties}
              />
              <b className={tone === 'ok' ? styles.ok : styles.warn}>0.{w}</b>
            </div>
          ))}
          <div className={styles.vglobal}>
            <span>global</span>
            <b className={styles.gradText}>0.94</b>
          </div>
        </div>
      ) : null}
      {visual === 'json' ? (
        <>
          <pre className={styles.vjson}>
            <code>
              <span className={styles.tokPun}>{'{ '}</span>
              <span className={styles.tokKey}>&quot;estado&quot;</span>
              <span className={styles.tokPun}>: </span>
              <span className={styles.tokStr}>&quot;listo&quot;</span>
              <span className={styles.tokPun}>,</span>
              {'\n  '}
              <span className={styles.tokKey}>&quot;confianza&quot;</span>
              <span className={styles.tokPun}>: </span>
              <span className={styles.tokStr}>&quot;alta&quot;</span>
              <span className={styles.tokPun}>,</span>
              {'\n  '}
              <span className={styles.tokKey}>&quot;datos&quot;</span>
              <span className={styles.tokPun}>: </span>
              <span className={styles.tokStr}>&quot;5 campos encontrados&quot;</span>
              <span className={styles.tokPun}>,</span>
              {'\n  '}
              <span className={styles.tokKey}>&quot;destino&quot;</span>
              <span className={styles.tokPun}>: </span>
              <span className={styles.tokStr}>&quot;tu sistema&quot;</span>
              <span className={styles.tokPun}>{' }'}</span>
            </code>
          </pre>
          <div className={styles.vwebhook}>
            <span className={styles.pulseDot} /> Documento terminado → enviado a tu sistema
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * The landing's "Cómo funciona" scrollytelling, driven by a step index instead
 * of scroll: the panel advances the beat text + visual as the live job/discovery
 * stage arrives. `steps` mirrors the backend pipeline 1:1 so the rail number and
 * name match what Azure Functions reports. Self-contained (CSS Module).
 */
export function HowItWorksFlow({ steps, activeIndex, caption }: HowItWorksFlowProps): JSX.Element {
  const last = steps.length - 1;
  const clamped = Math.max(0, Math.min(last, activeIndex));
  const active = steps[clamped] ?? steps[0];
  if (active === undefined) {
    return <div className={styles.root} />;
  }
  const railFill = last > 0 ? `${(clamped / last) * 100}%` : '0%';
  // Unique visuals to mount once (so crossfade works even when steps reuse one).
  const visuals: ReadonlyArray<FlowVisual> = ['invoice', 'scan', 'fields', 'checks', 'meters', 'json'];

  return (
    <div className={styles.root}>
      <div className={styles.grid}>
        <div className={styles.text}>
          <div className={styles.beats}>
            {steps.map((s, i) => (
              <div
                key={s.rail}
                className={clsx(styles.beat, i === clamped && styles.isOn)}
                aria-hidden={i !== clamped}
              >
                <h2>{s.h2}</h2>
                <p>{s.p}</p>
              </div>
            ))}
          </div>
          <div className={styles.rail} aria-hidden>
            <div className={styles.railLine}>
              <i style={{ height: railFill }} />
            </div>
            <ol>
              {steps.map((s, i) => (
                <li
                  key={s.rail}
                  className={clsx(i <= clamped && styles.isOn, i === clamped && styles.isActive)}
                >
                  <span className={styles.rn}>{String(i + 1).padStart(2, '0')}</span>
                  <span className={styles.rl}>{s.rail}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className={styles.visual}>
          <div className={styles.vcard}>
            <div className={styles.vcardTop}>
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.dot} />
              <span className={styles.vtitle}>{caption ?? 'documento.pdf'}</span>
              <span
                className={clsx(
                  styles.vbadge,
                  active.badge.tone === 'run' && styles.run,
                  active.badge.tone === 'ok' && styles.ok,
                )}
              >
                {active.badge.t}
              </span>
            </div>
            <div className={styles.vstage}>
              {visuals.map((v) => (
                <VisualState key={v} visual={v} active={v === active.visual} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
